import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, formatPence } from './ordering/orderingApi';
import usePageTitle from '@/hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ShoppingCart, X, Trash2, Plus, Minus, Ticket } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export default function OrderPage() {
  usePageTitle('Order');
  const [params] = useSearchParams();
  const seatFromQR = (params.get('seat') || '').toUpperCase();

  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [validSeats, setValidSeats] = useState([]);
  const [loading, setLoading] = useState(true);

  // Basket state
  const [basket, setBasket] = useState([]);
  const [basketOpen, setBasketOpen] = useState(false);

  // Checkout state
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [seat, setSeat] = useState(seatFromQR);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  // Raffle upsell
  const [raffleQuantity, setRaffleQuantity] = useState(0);
  
  // Donation
  const [donationAmount, setDonationAmount] = useState(0);
  const [customDonation, setCustomDonation] = useState('');
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/order/public-settings`),
      axios.get(`${API}/order/menu`),
      axios.get(`${API}/order/seats`),
    ])
      .then(([s, m, sl]) => {
        setSettings(s.data);
        setCategories(m.data.categories || []);
        setProducts(m.data.products || []);
        setValidSeats(sl.data.seats || []);
      })
      .catch(() => toast.error('Unable to load menu'))
      .finally(() => setLoading(false));
  }, []);

  const branding = (settings && settings.order_page_branding) || {};
  const bgColor = branding.background_color || '#f9fafb';
  const textColor = branding.text_color || '#111827';
  const buttonColor = branding.button_color || '#7c3aed';
  const buttonTextColor = branding.button_text_color || '#ffffff';
  const accentColor = branding.accent_color || buttonColor;
  const logoUrl = branding.logo_url || '';

  const addToBasket = (product) => {
    setBasket(prev => [...prev, {
      id: Date.now() + Math.random(),
      productId: product.id,
      name: product.name,
      price: product.price_pence
    }]);
    toast.success(`${product.name} added to basket`);
  };

  const removeFromBasket = (itemId) => {
    setBasket(prev => prev.filter(item => item.id !== itemId));
  };

  const basketTotal = basket.reduce((sum, item) => sum + item.price, 0);
  const basketCount = basket.length;

  const calculateRafflePrice = (qty) => {
    if (qty === 0) return 0;
    const deals = Math.floor(qty / 15);
    const remaining = qty % 15;
    const strips = Math.floor(remaining / 5);
    const singles = remaining % 5;
    return deals * 10 + strips * 5 + singles;
  };

  const rafflePrice = calculateRafflePrice(raffleQuantity);
  const grandTotal = basketTotal + (rafflePrice * 100) + donationAmount;

  const handleCheckout = () => {
    if (basket.length === 0) {
      toast.error('Your basket is empty');
      return;
    }
    setBasketOpen(false);
    setCheckoutOpen(true);
  };

  const handleSubmitOrder = async () => {
    // Validation
    if (!seat || !seat.trim()) {
      toast.error('Please enter your seat number');
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setSubmitting(true);

    try {
      // Build order payload
      const orderItems = basket.map(item => ({ productId: item.productId, quantity: 1 }));
      
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        seat: seat.trim().toUpperCase(),
        items: orderItems,
        donationPence: donationAmount,
        raffleQuantity: raffleQuantity
      };

      const response = await axios.post(`${API}/order/checkout`, payload);

      if (response.data.success) {
        if (response.data.checkoutUrl) {
          // Card payment - redirect to SumUp
          window.location.href = response.data.checkoutUrl;
        } else {
          // Cash payment or free order
          toast.success('Order placed successfully!');
          // Reset
          setBasket([]);
          setCheckoutOpen(false);
          setFirstName('');
          setLastName('');
          setSeat(seatFromQR);
          setRaffleQuantity(0);
          setDonationAmount(0);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Order failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading menu...</p>
      </div>
    );
  }

  if (!settings || settings.online_ordering_disabled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-bold mb-2">Ordering Currently Unavailable</h2>
            <p className="text-gray-600">Please check back later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: bgColor, color: textColor }}>
      {/* Fixed Basket Button - Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <Button
          onClick={() => setBasketOpen(true)}
          className="shadow-lg h-14 px-4 relative hover:opacity-90 transition-opacity"
          size="lg"
          style={{ backgroundColor: buttonColor, color: buttonTextColor }}
        >
          <ShoppingCart className="w-5 h-5 mr-2" />
          <div className="text-left">
            <div className="text-xs opacity-90">Basket</div>
            <div className="font-bold">{basketCount} items • {formatPence(basketTotal)}</div>
          </div>
          {basketCount > 0 && (
            <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
              {basketCount}
            </div>
          )}
        </Button>
      </div>

      {/* Menu */}
      <div className="max-w-4xl mx-auto p-4 pt-24">
        <div className="text-center mb-8">
          {logoUrl && (
            <img src={logoUrl} alt="" className="h-20 mx-auto mb-4 object-contain" />
          )}
          <h1 className="text-3xl font-bold mb-2">Menu</h1>
          <p className="opacity-80">Browse and add items to your basket</p>
        </div>

        {categories.map(category => {
          const categoryProducts = products.filter(p => p.category_id === category.id);

          if (categoryProducts.length === 0) return null;

          return (
            <div key={category.id} className="mb-8">
              <h2 className="text-2xl font-bold mb-4 border-b pb-2" style={{ borderColor: accentColor }}>{category.name}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {categoryProducts.map(product => (
                  <Card key={product.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-gray-900">{product.name}</h3>
                          {product.description && (
                            <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                          )}
                        </div>
                        <div className="text-xl font-bold ml-4" style={{ color: accentColor }}>
                          {formatPence(product.price_pence)}
                        </div>
                      </div>

                      <Button
                        onClick={() => addToBasket(product)}
                        className="w-full hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: buttonColor, color: buttonTextColor }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add to Basket
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}

        {products.length === 0 && (
          <div className="text-center py-12 opacity-70">
            <p>No items available at the moment.</p>
          </div>
        )}
      </div>

      {/* Basket Modal */}
      <Dialog open={basketOpen} onOpenChange={setBasketOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Your Basket ({basketCount} items)
            </DialogTitle>
          </DialogHeader>

          {basket.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <ShoppingCart className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Your basket is empty</p>
            </div>
          ) : (
            <div className="space-y-3">
              {basket.map(item => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium">{item.name}</div>
                    <div className="text-sm text-gray-600">{formatPence(item.price)}</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFromBasket(item.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Total:</span>
                  <span style={{ color: accentColor }}>{formatPence(basketTotal)}</span>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                className="w-full hover:opacity-90 transition-opacity"
                style={{ backgroundColor: buttonColor, color: buttonTextColor }}
                size="lg"
              >
                Proceed to Checkout
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout Modal */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Checkout</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Order Summary */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Order Summary</h3>
              {basket.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm py-1">
                  <span>{item.name}</span>
                  <span>{formatPence(item.price)}</span>
                </div>
              ))}
              <div className="border-t mt-2 pt-2 font-bold flex justify-between">
                <span>Subtotal:</span>
                <span>{formatPence(basketTotal)}</span>
              </div>
            </div>

            {/* Seat Number (only if not from QR) */}
            {!seatFromQR && (
              <div>
                <Label htmlFor="seat">Seat Number *</Label>
                <Input
                  id="seat"
                  value={seat}
                  onChange={(e) => setSeat(e.target.value.toUpperCase())}
                  placeholder="e.g., A12"
                  className="text-lg"
                />
              </div>
            )}

            {seatFromQR && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-sm text-blue-900">
                  <strong>Seat:</strong> {seat}
                </div>
              </div>
            )}

            {/* Customer Name */}
            <div>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
              />
            </div>

            <div>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Smith"
              />
            </div>

            {/* Raffle Upsell */}
            {settings && !settings.raffle_upsell_disabled && (
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Ticket className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold">Add Raffle Tickets?</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  £1 per number • £5 per strip (5) • £10 for 15 numbers
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setRaffleQuantity(Math.max(0, raffleQuantity - 1))}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    value={raffleQuantity}
                    onChange={(e) => setRaffleQuantity(Math.max(0, parseInt(e.target.value) || 0))}
                    className="text-center w-20"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setRaffleQuantity(raffleQuantity + 1)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600">
                    {raffleQuantity > 0 && `= £${rafflePrice}`}
                  </span>
                </div>
              </div>
            )}

            {/* Donation */}
            {settings && settings.donations_enabled && (
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Support Us</h3>
                <p className="text-sm text-gray-600 mb-3">
                  {settings.donation_message || 'Any support is greatly appreciated'}
                </p>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {[100, 200, 500].map(amount => (
                    <Button
                      key={amount}
                      variant={donationAmount === amount ? 'default' : 'outline'}
                      onClick={() => {
                        setDonationAmount(amount);
                        setCustomDonation('');
                      }}
                      style={donationAmount === amount ? { backgroundColor: buttonColor, color: buttonTextColor } : undefined}
                    >
                      {formatPence(amount)}
                    </Button>
                  ))}
                  <Input
                    type="number"
                    placeholder="Other"
                    value={customDonation}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomDonation(val);
                      setDonationAmount(val ? Math.round(parseFloat(val) * 100) : 0);
                    }}
                    className="text-center"
                  />
                </div>
              </div>
            )}

            {/* Grand Total */}
            <div className="p-4 rounded-lg border-2" style={{ backgroundColor: bgColor + '20', borderColor: accentColor }}>
              <div className="flex justify-between items-center text-xl font-bold">
                <span>Total to Pay:</span>
                <span style={{ color: accentColor }}>{formatPence(grandTotal)}</span>
              </div>
              {raffleQuantity > 0 && (
                <div className="text-xs text-gray-600 mt-1">
                  Includes {raffleQuantity} raffle ticket{raffleQuantity !== 1 ? 's' : ''}
                </div>
              )}
              {donationAmount > 0 && (
                <div className="text-xs text-gray-600">
                  Includes {formatPence(donationAmount)} donation - Thank you!
                </div>
              )}
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmitOrder}
              disabled={submitting}
              className="w-full hover:opacity-90 transition-opacity"
              style={{ backgroundColor: buttonColor, color: buttonTextColor }}
              size="lg"
            >
              {submitting ? 'Processing...' : 'Complete Order'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
